import React, { useEffect, useState } from "react";
import CustomButton from "../../../components/common/button/CustomButton";
import { useDispatch } from "react-redux";
import {
  addInvoiceAction,
  deleteInvoiceAction,
  downloadInvoiceAction,
  getSingleInvoiceAction,
  setDownloadInvoiceAction,
} from "../../../store/reducers/invoiceSlice";
import { invoiceValidateForm } from "./invoiceUntils";
import AlertPopup from "../../../components/common/alertPopup/AlertPopup";
import { useSelector } from "react-redux";
import { Alert, Grid } from "@mui/material";
import InvoiceAlertAction from "./InvoiceAlertAction";
import Notification from "../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";

interface InvoiceActionProps {
  formData: any;
  setFormData: any;
  isDisabled?: boolean;
  selectedInvoice?: any;
  isFormReadyOnly?: any;
  isCompletionInvoice?: boolean;
}

const popupTitle: any = {
  email: "Invoice Email",
  pdf: "Invoice PDF",
  odt: "Invoice ODT",
};

const popupContent: any = {
  email: "Are you sure you want to send an email?",
  pdf: "Are you sure you want to download the PDF?",
  odt: "Are you sure you want to download the IDT?",
  previous: "Are you sure you want to download it?",
};

const formatDateOnly = (value: any) => {
  if (!value) return value;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return value;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(value.getDate()).padStart(2, "0")}`;
  }

  const valueString = String(value).trim();
  const isoDate = valueString.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDate) return isoDate;

  const parsedDate = new Date(valueString);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return `${parsedDate.getFullYear()}-${String(
    parsedDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
};

const InvoiceAction: React.FC<InvoiceActionProps> = (props) => {
  const {
    formData,
    setFormData,
    isDisabled,
    selectedInvoice,
    isFormReadyOnly,
    isCompletionInvoice,
  } = props;
  const dispatch = useDispatch();
  const { leadDetails } = useSelector((state: any) => state.lead);
  const { payments } = useSelector((state: any) => state.payment);
  const { user, userProfile } = useSelector((state: any) => state?.user);
  const [open, setOpen] = useState(false);
  const [alertModel, setAlertModel] = useState(false);
  const [actionType, setActionType] = useState("");
  const [invoiceActionType, setInvoiceActionType] = useState<
    "copy" | "duplicate" | "restart" | "delete" | ""
  >("");

  const { isFileDownloaded, invoiceDetails } = useSelector(
    (state: any) => state.invoice,
  );

  const [validationErrors, setValidationErrors] = useState<any>({});

  // Function to filter out empty rows
  const filterEmptyRows = (items: any[]) => {
    return items.filter(
      (row) =>
        row.quantity ||
        row.description ||
        row.rate ||
        row.total ||
        row.vat_rate ||
        row.vat_value,
    );
  };

  const submitHandler = () => {
    console.log("formData-81", formData);
    const normalizedFormData = {
      ...formData,
      invoice_date:
        formatDateOnly(formData?.created_at) ||
        formatDateOnly(formData?.invoice_date),
      due_date: formatDateOnly(formData?.due_date),
    };

    // Perform validation
    const { isError, localError } = invoiceValidateForm(normalizedFormData);
    // If there are validation errors, set the errors in state and prevent submission
    if (isError) {
      setValidationErrors(localError);
      return;
    }

    // Filter empty rows before submission
    const filteredItems = filterEmptyRows(normalizedFormData.items);

    const normalizedItems = filteredItems.map((item: any) => ({
      ...item,
      quantity:
        item?.quantity === "" || item?.quantity === null || item?.quantity === undefined
          ? 0
          : Number(item.quantity),
      rate:
        item?.rate === "" || item?.rate === null || item?.rate === undefined
          ? 0
          : Number(item.rate),
      price:
        item?.price === "" || item?.price === null || item?.price === undefined
          ? 0
          : Number(item.price),
      vat_rate:
        item?.vat_rate === "" || item?.vat_rate === null || item?.vat_rate === undefined
          ? 0
          : Number(item.vat_rate),
      vat_value:
        item?.vat_value === "" || item?.vat_value === null || item?.vat_value === undefined
          ? 0
          : Number(item.vat_value),
      total:
        item?.total === "" || item?.total === null || item?.total === undefined
          ? 0
          : Number(item.total),
    }));

    const { created_at, ...invoiceData } = normalizedFormData;

    // Update formData with filtered items
    const updatedFormData = {
      ...invoiceData,
      items: normalizedItems,
      vat_amount:
        normalizedFormData.vat_amount === ""
          ? 0
          : normalizedFormData.vat_amount,
    };

    // No validation errors, proceed with form submission
    dispatch(addInvoiceAction(updatedFormData));
  };

  const onShow = (type: any) => {
    // setOpen(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    setActionType(type);
  };

  useEffect(() => {
    if (actionType) {
      handleDownloadInvoice();
      setActionType("");
    }
  }, [actionType]);

  const onHide = () => {
    setOpen(false);
  };

  const handleDownloadInvoice = () => {
    dispatch(
      downloadInvoiceAction({
        invoice_number: selectedInvoice
          ? selectedInvoice
          : formData.invoice_number,
        actionType: actionType === "pdf" ? "download" : "email",
      }),
    );
  };

  useEffect(() => {
    if (isFileDownloaded) {
      dispatch(setDownloadInvoiceAction(false));
      setOpen(false);
    }
  }, [isFileDownloaded]);

  const handleInvoice = (
    actionType: "copy" | "duplicate" | "restart" | "delete",
  ) => {
    setAlertModel(true);
    setInvoiceActionType(actionType);
  };

  const handleInvoiceAction = () => {
    // 🧩 Step 1: Select base data correctly
    let baseData: Record<string, any> = {};
  
    if (invoiceActionType === "copy") {
      // 👉 Use the currently edited form data
      baseData =
        formData && typeof formData === "object" && !Array.isArray(formData)
          ? { ...formData }
          : {};
    } else if (invoiceActionType === "duplicate") {
      // 👉 Use the originally fetched invoice details
      baseData =
        invoiceDetails && typeof invoiceDetails === "object"
          ? { ...invoiceDetails }
          : {};
    }

    // 🧹 Step 2: Remove fields not needed in new invoice
    const fieldsToRemove = [
      "invoice_number",
      "generated_id",
      "payment_id",
      "updated_at",
      "lead_status",
    ];
    fieldsToRemove.forEach((field) => delete baseData[field]);

    const invoiceDate = baseData.created_at
      ? String(baseData.created_at).split("T")[0]
      : baseData.created_at;
    delete baseData.created_at;
    if (invoiceDate) {
      baseData.invoice_date = invoiceDate;
    }

    // 🏷 Step 3: Always link the reference invoice number (for traceability)
    baseData.reference_invoice_number = invoiceDetails?.invoice_number;

    // 🧾 Step 4: Mark the action type
    if (invoiceActionType === "copy") {
      baseData.is_copy = true;
      baseData.status ="Outstanding";
    } else if (invoiceActionType === "duplicate") {
      baseData.is_duplicate = true;
       baseData.status ="Outstanding";
    } else if (invoiceActionType === "restart") {
      baseData.is_restart = true;
    }
    // ✅ Step 5: Final check before dispatch
    console.log("baseData",baseData);
    dispatch(addInvoiceAction(baseData));
    setAlertModel(false);
    setInvoiceActionType("");
  };

  const handleDeleteInvoiceAction = () => {
    dispatch(
      deleteInvoiceAction({
        invoice_number: selectedInvoice ?? formData?.invoice_number,
      }),
    );
    setAlertModel(false);
    setInvoiceActionType("");
  };

  const handleRestartInvoiceAction = () => {
    const resolvedInvoiceType =
      (formData?.invoice_type &&
      String(formData.invoice_type).toLowerCase() !== "general"
        ? formData.invoice_type
        : formData?.category === "final"
          ? "final"
          : formData?.invoice_type) || "general";

    const payload: any = {
      isRestart: true,
      invoiceNo: selectedInvoice || null,
      invoiceType: resolvedInvoiceType,
      leadNo: formData?.lead_id || leadDetails?.lead?.generated_id || null,
      quoteId: formData?.quote_id || null,
    };
    dispatch(getSingleInvoiceAction(payload));
  };

  useEffect(() => {
    if (invoiceDetails?.invoice_number) {
      setAlertModel(false);
      setInvoiceActionType("");
    }
    setAlertModel(false);
  }, [invoiceDetails]);

  const roles = user?.user?.roles?.map((role: any) => role.toLowerCase()) || [];

  const isOwnerOrTenant =
    roles.includes("tenant") ||
    roles.includes("owner") ||
    roles.includes("admin");

    console.log("validationErrors",validationErrors);
  return (
    <>
      <Notification />
      {alertModel && (
        <InvoiceAlertAction
          alertModalShow={alertModel}
          setAlertModelShow={setAlertModel}
          selectedPaymentId={formData?.payment_id}
          message={
            invoiceActionType === "copy"
              ? "Are you sure you want to copy this invoice?"
              : invoiceActionType === "duplicate"
                ? "Are you sure you want to duplicate this invoice?"
                : invoiceActionType === "delete"
                  ? "Are you sure you want to delete this invoice?"
                  : "Are you sure you want to restart this invoice?"
          }
          onSubmit={() =>
            invoiceActionType === "delete"
              ? handleDeleteInvoiceAction()
              : invoiceActionType === "restart"
                ? handleRestartInvoiceAction()
                : handleInvoiceAction()
          }
        />
      )}
      <Grid container spacing={0.5} sx={{ mt: 1 }}>
        {!(
          isFormReadyOnly ||
          formData?.quick_pay === 0 ||
          (formData?.invoice_type === "deposit" &&
            leadDetails?.lead?.status === "JOB")
        ) &&
          formData?.net &&
          formData?.due_date && (
            <Alert severity="info" sx={{ mb: 1, width: "100%" }}>
              If you have made any changes, please save them before taking
              further action.
            </Alert>
          )}
        <div style={{ marginBottom: "10px", marginTop: "10px" }}>
          <CustomButton
            disabled={
              isFormReadyOnly ||
              formData?.quick_pay === 0 ||
              (formData?.invoice_type === "deposit" &&
                leadDetails?.lead?.status === "JOB")
                ? true
                : formData?.net && formData?.due_date
                  ? false
                  : true
            }
            style={{ marginRight: "10px" }}
            title="Save"
            //   disabled={estimateForm?.quoteId ? false : true}
            onClick={() => submitHandler()}
          />
          {/* Todo */}
          {/* <CustomButton
          disabled={isFormReadyOnly ? true : isDisabled}
          style={{ marginRight: "10px" }}
          title="Update"
          //   disabled={estimateForm?.quoteId ? false : true}
          //   onClick={() => handleInvoice()}
        /> */}
          {/* <CustomButton
          disabled={isFormReadyOnly ? true : isDisabled}
          style={{ marginRight: "10px" }}
          title="New"
          //   disabled={estimateForm?.quoteId ? false : true}
          //   onClick={() => handleInvoice()}
        />{" "} */}
          {formData?.invoice_type !== "credit" && (
            <>
              <CustomButton
                disabled={
                  (formData?.invoice_number &&
                    formData?.status === "Paid" &&
                    !isOwnerOrTenant) ||
                  !formData?.invoice_number
                }
                style={{ marginRight: "10px" }}
                title="Copy"
                onClick={() => handleInvoice("copy")}
              />{" "}
              <CustomButton
                disabled={
                  (formData?.invoice_number &&
                    formData?.status === "Paid" &&
                    !isOwnerOrTenant) ||
                  !formData?.invoice_number
                }
                style={{ marginRight: "10px" }}
                title="Duplicate"
                onClick={() => handleInvoice("duplicate")}
              />{" "}
              <CustomButton
                disabled={
                  (formData?.invoice_number &&
                    (formData?.category === "final" ||
                      formData?.category === "general") &&
                    ((formData?.status === "Paid" && !isOwnerOrTenant) ||
                      payments.filter((p: any) => !p.is_advance).length > 0)) ||
                  !isCompletionInvoice
                }
                style={{ marginRight: "10px" }}
                title="Restart"
                onClick={() => handleInvoice("restart")}
              />{" "}
            </>
          )}
          <CustomButton
            disabled={
              formData?.invoice_number && payments.length === 0 ? false : true
            }
            style={{ marginRight: "10px" }}
            title="Delete"
            onClick={() => handleInvoice("delete")}
          />{" "}
          {/* <CustomButton
          disabled={isFormReadyOnly ? true : isDisabled}
          style={{ marginRight: "10px" }}
          title="Confirm"
          //   disabled={estimateForm?.quoteId ? false : true}
          //   onClick={() => handleInvoice()}
        />{" "} */}
          <CustomButton
            // disabled={formData?.invoice_number ? false : true}
            style={{ marginRight: "10px" }}
            title="PDF"
            onClick={() => onShow("pdf")}
          />{" "}
          {/* Todo */}
          {/* <CustomButton
          disabled={isFormReadyOnly ? true : isDisabled}
          style={{ marginRight: "10px" }}
          title="ODT"
          //   disabled={estimateForm?.quoteId ? false : true}
          //   onClick={() => handleInvoice()}
        /> */}
          <CustomButton
            //  disabled={formData?.invoice_number ?  false : true  }
            style={{ marginRight: "10px" }}
            title="Email"
            onClick={() => onShow("email")}
          />
        </div>
        <AlertPopup
          open={open}
          onHide={onHide}
          title={popupTitle[actionType]}
          content={popupContent[actionType]}
          submitHandler={handleDownloadInvoice}
        />
      </Grid>
    </>
  );
};
export default InvoiceAction;
